import {
  Menu,
  MenuButton,
  Button,
  MenuList,
  MenuItem,
  Box,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  ModalFooter,
  Center,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { useLoanActions } from "providers/loans/mutations/loan-actions";
import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loan } from "types/property";
import { api } from "utils/api";
import { stringToDollar } from "utils/helpers";

interface BindLoanButtonProps {
  propertyId: string;
  setRefreshTable?: (refreshTable: boolean) => void;
}

export function BindLoanButton({ propertyId, setRefreshTable }: BindLoanButtonProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loans, setLoans] = useState<Loan[]>([]);
  const [filteredLoans, setFilteredLoans] = useState<Loan[]>([]);
  const [selectedLoans, setSelectedLoans] = useState<string[]>([]);
  const toast = useToast();

  const { applyChanges } = useLoanActions();

  const fetchLoans = useCallback(async (): Promise<Loan[]> => {
    setLoading(true);
    const result = await api.get("/supabase/loans/findLoansWithNoProperty");
    setLoading(false);
    return result.data ?? [];
  }, []);

  const NewApplicationLink = () => <Link to="/loans/application">New loan application</Link>;

  const updateLoansWithPropertyId = async (propertyId: string, loanIds: string[]) => {
    try {
      for (const loanId of loanIds) {
        await applyChanges.mutateAsync({
          id: loanId,
          property: propertyId,
        });
      }
      toast({
        title: "Loans updated",
        description: "Loans updated successfully",
        status: "success",
      });
    } catch (error) {
      toast({
        title: "Error updating loans",
        description: "Error updating loans",
        status: "error",
      });
    }
  };

  const handleSave = async () => {
    if (selectedLoans.length > 0 && propertyId) {
      setSaving(true);
      await updateLoansWithPropertyId(propertyId, selectedLoans);
      onClose();
      triggerTableRefresh();
      setSaving(false);
    }
  };

  const triggerTableRefresh = () => {
    setRefreshTable?.(true);
  };

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value.toLowerCase();
    setSearchInput(searchTerm);

    const filtered = loans.filter(loan => loan.name.toLowerCase().includes(searchTerm));
    setFilteredLoans(filtered);
  };

  const handleLoanSelection = (loanId: string) => {
    setSelectedLoans(prevSelectedLoans => {
      const isAlreadySelected = prevSelectedLoans.includes(loanId);
      if (isAlreadySelected) {
        return prevSelectedLoans.filter(id => id !== loanId);
      } else {
        return [...prevSelectedLoans, loanId];
      }
    });
  };

  const isSelected = (loanId: string) => {
    return selectedLoans.includes(loanId);
  };

  const handleOpenModal = async () => {
    onOpen();
    const loans = await fetchLoans();
    setLoans(loans ?? []);
    setFilteredLoans(loans ?? []);
  };

  return (
    <>
      <Menu>
        <MenuButton as={Button} variant="secondary" size="sm">
          Add Loan
        </MenuButton>
        <MenuList>
          <MenuItem>
            <NewApplicationLink />
          </MenuItem>
          <MenuItem onClick={handleOpenModal}>Search Existing Loans</MenuItem>
        </MenuList>
      </Menu>
      {isOpen && (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl">
          <ModalOverlay />
          <ModalContent>
            <ModalCloseButton />
            <ModalHeader>Adding Existing loans to property</ModalHeader>
            <ModalBody>
              <Flex direction="column" gap={4}>
                <Text color="black" fontSize="14px" fontWeight="500">
                  Select loan and add them to the property
                </Text>
                <FormControl isRequired>
                  <FormLabel>Search for Loans</FormLabel>
                  <Input
                    variant="primary"
                    placeholder="Search existing loans..."
                    value={searchInput}
                    onChange={handleSearchInputChange}
                  ></Input>
                </FormControl>

                <Box
                  maxHeight="300px"
                  overflowY="auto"
                  sx={{
                    "&::-webkit-scrollbar": {
                      width: "3px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "gray.200",
                      borderRadius: "8px",
                      height: "150px",
                    },
                  }}
                >
                  {loading ? (
                    <Center>
                      <Spinner />
                    </Center>
                  ) : (
                    <Flex direction="column" gap={2} mr={4}>
                      {filteredLoans.map(loan => (
                        <Flex
                          key={loan.id || "Not Provided"}
                          border="2px solid #D4D4D4"
                          padding={4}
                          borderRadius="12px"
                          opacity={isSelected(loan.id) ? "0.5" : "1"}
                          pointerEvents={isSelected(loan.id) ? "none" : "auto"}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Flex gap={2} alignItems="center">
                            <Text maxWidth={"300px"} textAlign="left" color="black">
                              {loan.name || "Not Provided"}
                            </Text>
                            <Text textAlign="right" marginRight={"45px"} color="black">
                              {stringToDollar((loan.amount ?? 0).toString()) || "Not Provided"}
                            </Text>
                          </Flex>
                          <Button
                            size="sm"
                            onClick={() => handleLoanSelection(loan.id)}
                            disabled={isSelected(loan.id)}
                          >
                            Select
                          </Button>
                        </Flex>
                      ))}
                    </Flex>
                  )}
                </Box>
              </Flex>
            </ModalBody>
            <ModalFooter>
              <Button
                onClick={() => handleSave()}
                isDisabled={selectedLoans.length === 0}
                isLoading={saving}
              >
                Save
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}
